/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useParams } from "react-router-dom";
import { v4 } from "uuid";
import { Box, Text, Switch, Flex } from "@nordcloud/gnui";
import { FlexContainer, QueryPagination } from "~/components";
import { AddApplication } from "~/components/Applications/ApplicationAdd";
import { ApplicationItem } from "~/components/Applications/ApplicationItem";
import { useShowError } from "~/components/Error/useShowError";
import { ExpandableTag } from "~/components/ExpandableTag/ExpandableTag";
import { GeneralServiceButton } from "~/components/GeneralService/GeneralServiceButton";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { ApplicationSearch } from "~/components/Search/ApplicationSearch";
import { RouteParams } from "~/models";
import { Application } from "~/models/application";
import { useAxios, useQueryState } from "~/tools";

function Applications() {
  const { customerId } = useParams<RouteParams>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [showRemoved, setShowRemoved] = React.useState(false);

  const [
    { limit, page, query },
    ignored,
    updateQueryState,
    removeFilter,
  ] = useQueryState();

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const [applications, setApplications] = React.useState({
    list: [],
    totalCount: "",
  });

  const [generalEnvId, setGeneralEnvId] = React.useState<string | undefined>();

  const toggleShowRemoved = () => {
    setShowRemoved(!showRemoved);
    updateQueryState({ page: "0" });
  };

  const fetchApplications = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const removed = showRemoved.toString();
      const params = { customerId, limit, page, removed, query };
      const { data } = await Axios().get("/applications", params);
      setApplications(() => ({
        list: data.applications,
        totalCount: data.totalCount,
      }));
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [showRemoved, Axios, customerId, limit, page, query, showError]);

  React.useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const fetchGeneralEnvID = React.useCallback(async () => {
    try {
      const url = `/customers/${customerId}/generalmapping`;
      const { data } = await Axios().get(url);
      setGeneralEnvId(data?.environmentId);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, customerId, showError]);

  React.useEffect(() => {
    fetchGeneralEnvID();
  }, [fetchGeneralEnvID]);

  type AppId = {
    idV4: string;
    app: Application;
  };
  const applicationsAddId = applications?.list
    ?.map((app) => ({ idV4: v4(), app }))
    .filter((app: AppId) => showRemoved || !app.app.removed);

  return (
    <Box>
      <div css={{ margin: "0 1rem 1rem auto" }}>
        <FlexContainer justify="flex-end">
          <ApplicationSearch removed={showRemoved} />
          <AddApplication refetch={fetchApplications} />
        </FlexContainer>

        <Flex css={{ marginLeft: "0.75rem" }}>
          <Switch
            checked={showRemoved}
            labelText="Show removed"
            onChange={toggleShowRemoved}
          />

          {query && (
            <>
              <div css={{ width: "8px" }} />
              <ExpandableTag
                css={{ marginLeft: "1rem" }}
                name="query"
                value={query as any}
                onRemoveKey={() => removeFilter(["query"])}
              />
            </>
          )}
        </Flex>
      </div>

      <LoaderWrap loading={isLoading}>
        {generalEnvId && (
          <GeneralServiceButton
            generalEnvId={generalEnvId}
            customerId={customerId}
          />
        )}
        {applicationsAddId?.length > 0 ? (
          <>
            {applicationsAddId.map((a: AppId) => (
              <ApplicationItem
                fetchApplications={fetchApplications}
                key={a.idV4}
                application={a.app}
              />
            ))}
            <QueryPagination count={Number(applications?.totalCount) || 0} />
          </>
        ) : (
          <Text>No applications added.</Text>
        )}
      </LoaderWrap>
    </Box>
  );
}

export { Applications };
